"use client";

import { useEffect, useRef, useState, ViewTransition } from "react";
import Script from "next/script";
import { logInfo } from "@utils/logger";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";
import { CSSTransition } from "react-transition-group";
import { PARENT_STORE_NAME } from "@app/constants";
import ROUTES from "@app/routes";
import { RootState } from "@app/store";
import { openCookieConfirmIfNeeded } from "@components/cookies-confirm/open-cookie-confirm-if-needed";
import { EModalKeys, useModal } from "@components/helpers/modal-provider";
import { HOME_PRELOADER_AFTER_100_EVENT, HOME_PRELOADER_BEFORE_HIDE_EVENT } from "@components/page-loader/constants";
import { MainButton } from "@components/ui-kit/elements/buttons/main-button/main-button";
import { ScrollableViewport } from "@components/ui-kit/organisms/scrollable-viewport/scrollable-viewport";
import { useLazyGetConfigQuery } from "@entities/cms/cms-api";
import { getTimeShowSeconds } from "@entities/cms/get-time-show";
import { HomeGlobalSettings, HomeItem } from "@entities/home/helpers";
import useDataLayerAnalytics from "@hooks/use-analytics";
import useAnimatedRouter from "@hooks/use-animated-router";
import { useMicroMarkup } from "@hooks/use-micro-markup";
import HomePageView from "@views/home";
import homeStyles from "@views/home/home.module.scss";
import { PAGE_SCROLL_CONTENT_VT_NAME } from "../../../shared/lib/navigation";
import "shared/rich-styles/rich-common.scss";
type HomePageClientProps = {
  initialHomeItems: HomeItem[];
  homeGlobalSettings: HomeGlobalSettings;
};
const getHomePageScrollViewTransitionName = () => {
  if (typeof document === "undefined") return undefined;
  const direction = document.documentElement.dataset.routeDirection;
  return direction === "up" || direction === "down" ? PAGE_SCROLL_CONTENT_VT_NAME : undefined;
};
const HomePageClient = ({
  initialHomeItems,
  homeGlobalSettings
}: HomePageClientProps) => {
  const sloganNodeRef = useRef<HTMLDivElement>(null);
  const buysButtonNodeRef = useRef<HTMLDivElement>(null);
  const isSloganCycleArmedRef = useRef<boolean>(false);
  const dispatch = useDispatch();
  const [fetchConfig] = useLazyGetConfigQuery();
  const {
    navigate
  } = useAnimatedRouter();
  const {
    activeModalKey
  } = useModal();
  const [isSloganFadeActive, setIsSloganFadeActive] = useState(false);
  const [isSloganFaded, setIsSloganFaded] = useState(true);
  const [pageScrollViewTransitionName] = useState(getHomePageScrollViewTransitionName);
  const isMenuOpen = useSelector((state: RootState) => state.ui.isMenuOpen);
  const isAuthPopupOpen = activeModalKey === EModalKeys.AUTH;
  const {
    setDataLayerAction
  } = useDataLayerAnalytics();
  const microMarkup = useMicroMarkup({
    type: "WebSite",
    data: null
  });
  useEffect(() => {
    logInfo("[home-page] SSR home-items", {
      count: initialHomeItems.length,
      items: initialHomeItems,
      globalSettings: homeGlobalSettings
    });
  }, [initialHomeItems, homeGlobalSettings]);
  useEffect(() => {
    const handleBeforeHide = () => {
      isSloganCycleArmedRef.current = true;
      setIsSloganFaded(false);
    };
    const handleAfter100 = () => {
      if (!isSloganCycleArmedRef.current) return;
      setIsSloganFadeActive(true);
    };
    window.addEventListener(HOME_PRELOADER_BEFORE_HIDE_EVENT, handleBeforeHide);
    window.addEventListener(HOME_PRELOADER_AFTER_100_EVENT, handleAfter100);
    return () => {
      window.removeEventListener(HOME_PRELOADER_BEFORE_HIDE_EVENT, handleBeforeHide);
      window.removeEventListener(HOME_PRELOADER_AFTER_100_EVENT, handleAfter100);
    };
  }, [isSloganFaded, dispatch]);

  /** INFO [a.savrov 15-07-2026 - 15:22]:
   * Вынесен в отдельный useEffect с минимумом зависимостей, чтобы не умирал от изменения зависимостей
   */
  useEffect(() => {
    let cookieTimerId: ReturnType<typeof setTimeout> | null = null;
    let abortTimeShow: (() => void) | null = null;
    const init = () => {
      const {
        promise,
        abort
      } = getTimeShowSeconds(fetchConfig);
      abortTimeShow = abort;
      promise.then(seconds => {
        if (seconds > 0) {
          cookieTimerId = setTimeout(() => openCookieConfirmIfNeeded(dispatch), seconds * 1000);
        } else {
          openCookieConfirmIfNeeded(dispatch);
        }
      }).catch(() => {
        openCookieConfirmIfNeeded(dispatch);
      });
    };
    window.addEventListener(HOME_PRELOADER_BEFORE_HIDE_EVENT, init);
    return () => {
      abortTimeShow?.();
      if (cookieTimerId !== null) {
        clearTimeout(cookieTimerId);
      }
      window.removeEventListener(HOME_PRELOADER_BEFORE_HIDE_EVENT, init);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
  useEffect(() => {
    if (!isSloganFadeActive) return;
    if (!isSloganCycleArmedRef.current) return;
    const timerId = window.setTimeout(() => {
      setIsSloganFadeActive(false);
      setIsSloganFaded(true);
      isSloganCycleArmedRef.current = false;
    }, 1750);
    return () => {
      window.clearTimeout(timerId);
    };
  }, [isSloganFadeActive]);
  return <>
      <Script id="breadcrumbs-ld-json" type="application/ld+json" dangerouslySetInnerHTML={microMarkup} />
      <h1 className="visually-hidden">Мерч {PARENT_STORE_NAME}</h1>
      <div className={clsx(homeStyles.container, "home__container")}>
        <div className={homeStyles.content}>
          <CSSTransition in={!isMenuOpen} timeout={250} classNames={{
          enter: homeStyles.homeSloganFadeEnter,
          enterActive: homeStyles.homeSloganFadeEnterActive,
          exit: homeStyles.homeSloganFadeExit,
          exitActive: homeStyles.homeSloganFadeExitActive
        }} unmountOnExit nodeRef={sloganNodeRef}>
            <div ref={sloganNodeRef} className={clsx(homeStyles.slogan, isSloganFadeActive && homeStyles.sloganFadeActive, isSloganFaded && homeStyles.sloganFaded)}>
              <span>мерч</span>
              <span>по другим</span>
              <span>правилам</span>
            </div>
          </CSSTransition>
          <div className={homeStyles.footer}>
            <CSSTransition in={!isMenuOpen} timeout={300} classNames={{
            enter: homeStyles.homeToBuysSlideEnter,
            enterActive: homeStyles.homeToBuysSlideEnterActive,
            exit: homeStyles.homeToBuysSlideExit,
            exitActive: homeStyles.homeToBuysSlideExitActive
          }} unmountOnExit nodeRef={buysButtonNodeRef}>
              <div ref={buysButtonNodeRef}>
                <MainButton size="large" variant="secondary" label="к покупкам" onClick={() => {
                setDataLayerAction({
                  action: "click",
                  eventLabel: "toShopping",
                  eventLocation: "mainPage"
                });
                navigate(ROUTES.CATALOG);
              }} />
              </div>
            </CSSTransition>
          </div>
        </div>
      </div>
      <ViewTransition default="none">
        <ScrollableViewport dragEnabled enabled={!isAuthPopupOpen} viewTransitionName={pageScrollViewTransitionName}>
          <HomePageView initialItems={initialHomeItems} homeGlobalSettings={homeGlobalSettings} isMenuOpen={isMenuOpen} />
        </ScrollableViewport>
      </ViewTransition>
    </>;
};
export default HomePageClient;